// Seo — runtime per-page meta updater for the marketing SPA.
// Updates document.title, meta description, canonical and OG tags
// when a page mounts. Strict 155-char description cap enforced.

import { useEffect } from 'react';

interface Props {
  title: string;
  description: string;
  path: string; // pathname starting with "/"
  noindex?: boolean;
}

const ORIGIN = 'https://ai.wavefrontstudiollc.com';

function setMeta(selector: string, attr: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, name] = selector.match(/\[(?:name|property)="([^"]+)"\]/) || [];
    if (selector.includes('property=')) {
      el.setAttribute('property', name || '');
    } else {
      el.setAttribute('name', name || '');
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function Seo({ title, description, path, noindex }: Props) {
  useEffect(() => {
    const desc = description.length > 155 ? description.slice(0, 152).trimEnd() + '...' : description;
    const url = ORIGIN + path;

    document.title = title;
    setMeta('meta[name="description"]', 'content', desc);
    setCanonical(url);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', desc);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', desc);
    // Whole site is intentionally not indexed; the per-page flag is kept for API compatibility.
    void noindex;
    setMeta('meta[name="robots"]', 'content', 'noindex,nofollow,noarchive');
  }, [title, description, path, noindex]);

  return null;
}
